import { Icon } from '@/components/Icon';
import { SeverityBadge } from '@/components/primitives';
import { findChannel } from '@/lib/channels';
import type { NotificationChannel, NotificationRule, WorkflowSummary } from '@/types/sturm';

interface PreviewProps {
  rule: Partial<NotificationRule>;
  channels: NotificationChannel[];
  workflow?: WorkflowSummary;
}

// The thing people read at 3am. Rendering it inside the editor is the point: a rule is only
// as good as the message it produces.
export function AlertMessagePreview({ rule, channels, workflow }: PreviewProps) {
  const name = workflow?.name ?? 'This workflow';
  const slug = workflow?.slug ?? 'workflow-slug';
  const queue = workflow?.queue ?? 'default-queue';
  const step = rule.parameters?.steps?.[0] ?? 'a step';
  const p = rule.parameters ?? {};

  const title = titleFor(rule.condition, name, step, p, queue);
  const body = bodyFor(rule.condition, workflow, p);

  const targets = (rule.channelIds ?? [])
    .map((id) => findChannel(channels, id)?.name)
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="msg">
      <div className="msg-hd">
        <Icon name="bell" size={12} />
        <span>Sturm{targets ? ` · ${targets}` : ''}</span>
        <span style={{ marginLeft: 'auto' }} className="mono">
          now
        </span>
      </div>
      <div className="msg-body">
        <div className={`msg-bar ${rule.severity ?? 'critical'}`} />
        <div className="flex1" style={{ minWidth: 0 }}>
          <div className="row gap-8">
            <SeverityBadge severity={rule.severity ?? 'critical'} />
            <span className="tiny muted mono">{workflow?.app ?? 'sturm'}</span>
          </div>
          <div className="msg-t" style={{ marginTop: 7 }}>
            {title}
          </div>
          <div className="msg-p">{body}</div>
          <div className="msg-facts">
            <Fact label="Workflow" value={slug} />
            <Fact label="Run" value="latest matching run" />
            <Fact label="Step" value={step} />
            <Fact label="Queue" value={queue} />
          </div>
          <div className="msg-btns">
            <span className="msg-btn">
              <Icon name="eye" size={12} /> Open run
            </span>
            <span className="msg-btn">
              <Icon name="refresh" size={12} /> Retry from step
            </span>
            <span className="msg-btn">
              <Icon name="check" size={12} /> Acknowledge
            </span>
            <span className="msg-btn">
              <Icon name="bellOff" size={12} /> Mute 1h
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="msg-fact">
      {label}
      <b>{value}</b>
    </div>
  );
}

type Params = NonNullable<NotificationRule['parameters']>;

function titleFor(
  condition: NotificationRule['condition'] | undefined,
  name: string,
  step: string,
  p: Params,
  queue: string,
): string {
  switch (condition) {
    case 'run-failed':
      return `${name} failed at ${step}`;
    case 'step-retrying':
      return `${step} retried ${p.attempts ?? 3}× on ${name}`;
    case 'run-stuck':
      return `${name} run stuck for ${p.runningLongerThan ?? '1h'}`;
    case 'failure-rate':
      return `${name} failure rate above ${p.failurePercent ?? 10}%`;
    case 'schedule-missed':
      return `${name} missed its scheduled start`;
    case 'queue-backlog':
      return `${queue} backlog above ${p.queuedTasks ?? 200} tasks`;
    case 'terminated':
      return `${name} run terminated by a person`;
    default:
      return `${name} alert`;
  }
}

function bodyFor(
  condition: NotificationRule['condition'] | undefined,
  workflow: WorkflowSummary | undefined,
  p: Params,
): string {
  switch (condition) {
    case 'run-failed':
      return 'Retry budget exhausted. The run stopped at the step above with its last error attached.';
    case 'step-retrying':
      return 'The same failure class is repeating — the step is failing faster than it recovers.';
    case 'run-stuck':
      return `No heartbeat from the worker since the step started. Execution timeout is ${workflow?.timeout ?? 'the workflow limit'}.`;
    case 'failure-rate':
      return 'Enough runs failed inside the window to clear the threshold. The dominant error is attached.';
    case 'schedule-missed':
      return `Expected a run at ${workflow?.trigger.detail ?? 'the scheduled time'}; nothing started within the grace period.`;
    case 'queue-backlog':
      return `Workers are saturated — more than ${p.queuedTasks ?? 200} tasks are waiting longer than they run.`;
    case 'terminated':
      return 'Terminated by a person. Compensation steps did not run.';
    default:
      return '';
  }
}
